import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  CircularProgress,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import type { RootState, AppDispatch } from '../../store/store';
import {
  fetchTaskLogs,
  fetchExecutionLogs,
  setSelectedTask,
  setSelectedExecution,
  clearTaskLogs,
  clearExecutionLogs,
} from '../../store/slices/tasksSlice';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div role='tabpanel' hidden={value !== index} id={`tabpanel-${index}`} aria-labelledby={`tab-${index}`} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const TaskLogViewer: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const { selectedTaskId, selectedExecutionId, taskLogs, executionLogs, loading } = useSelector((state: RootState) => state.tasks);
  const [tabValue, setTabValue] = useState<number>(0);
  const [limit, setLimit] = useState<number>(10);

  useEffect(() => {
    if (selectedTaskId && !taskLogs) {
      dispatch(fetchTaskLogs({ taskId: selectedTaskId, limit }));
    }
  }, [dispatch, selectedTaskId, taskLogs, limit]);

  const handleChangeTab = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleRefreshLogs = () => {
    if (selectedTaskId) {
      dispatch(fetchTaskLogs({ taskId: selectedTaskId, limit }));
    }
  };

  const handleClose = () => {
    dispatch(setSelectedTask(null));
    dispatch(clearTaskLogs());
    dispatch(clearExecutionLogs());
  };

  const handleLimitChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newLimit = parseInt(event.target.value, 10);
    if (!isNaN(newLimit) && newLimit > 0) {
      setLimit(newLimit);
      if (selectedTaskId) {
        dispatch(fetchTaskLogs({ taskId: selectedTaskId, limit: newLimit }));
      }
    }
  };

  const handleExecutionChange = (event: SelectChangeEvent) => {
    const executionId = event.target.value;
    if (executionId && selectedTaskId) {
      dispatch(setSelectedExecution(executionId));
      dispatch(fetchExecutionLogs({ taskId: selectedTaskId, executionId }));
    }
  };

  // If no task is selected, don't render anything
  if (!selectedTaskId) return null;

  return (
    <Paper variant='outlined' sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant='h6'>Task Logs</Typography>
        <Box>
          <Button startIcon={<RefreshIcon />} onClick={handleRefreshLogs} disabled={loading} size='small' sx={{ mr: 1 }}>
            Refresh
          </Button>
          <Button startIcon={<CloseIcon />} onClick={handleClose} size='small' color='inherit'>
            Close
          </Button>
        </Box>
      </Box>

      <Tabs value={tabValue} onChange={handleChangeTab} aria-label='log viewer tabs'>
        <Tab label='All Executions' id='tab-0' aria-controls='tabpanel-0' />
        <Tab label='Single Execution' id='tab-1' aria-controls='tabpanel-1' />
      </Tabs>

      <TabPanel value={tabValue} index={0}>
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
          <TextField
            label='Limit'
            type='number'
            size='small'
            value={limit}
            onChange={handleLimitChange}
            InputProps={{ inputProps: { min: 1 } }}
            sx={{ width: '100px', mr: 2 }}
          />
          <Button variant='outlined' size='small' onClick={handleRefreshLogs} disabled={loading}>
            Apply
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : taskLogs ? (
          <Box>
            <Typography variant='subtitle2' sx={{ mb: 1 }}>
              Found logs for {taskLogs.executionCount} executions
            </Typography>
            <Box sx={{ maxHeight: '400px', overflow: 'auto' }}>
              {Object.entries(taskLogs.logs).length > 0 ? (
                Object.entries(taskLogs.logs).map(([executionId, log]) => (
                  <Box key={executionId} sx={{ mb: 2 }}>
                    <Typography variant='body2' fontWeight='bold' sx={{ mb: 0.5 }}>
                      Execution: {executionId}
                    </Typography>
                    <Paper
                      variant='outlined'
                      sx={{
                        p: 1,
                        backgroundColor: '#151515',
                        fontFamily: 'monospace',
                        fontSize: '0.8rem',
                        whiteSpace: 'pre-wrap',
                        overflowX: 'auto',
                      }}
                    >
                      {log}
                    </Paper>
                  </Box>
                ))
              ) : (
                <Typography>No logs available</Typography>
              )}
            </Box>
          </Box>
        ) : (
          <Typography>No logs available</Typography>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Box sx={{ mb: 2 }}>
          <FormControl fullWidth size='small'>
            <InputLabel id='execution-select-label'>Execution</InputLabel>
            <Select
              labelId='execution-select-label'
              value={selectedExecutionId || ''}
              label='Execution'
              onChange={handleExecutionChange}
              disabled={!taskLogs || loading}
            >
              {taskLogs &&
                Object.keys(taskLogs.logs).map(executionId => (
                  <MenuItem key={executionId} value={executionId}>
                    {executionId}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : executionLogs ? (
          <Paper
            variant='outlined'
            sx={{
              p: 1,
              backgroundColor: '#151515',
              fontFamily: 'monospace',
              fontSize: '0.8rem',
              whiteSpace: 'pre-wrap',
              overflowX: 'auto',
              maxHeight: '400px',
              overflow: 'auto',
            }}
          >
            {executionLogs.logs}
          </Paper>
        ) : (
          <Typography>Select an execution to view logs</Typography>
        )}
      </TabPanel>
    </Paper>
  );
};

export default TaskLogViewer;
